import {
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CrossCircledIcon,
  MagnifyingGlassIcon,
} from '@radix-ui/react-icons';
import { Select as RdxSelect } from 'radix-ui';
import { useMemo, useRef, useState } from 'react';
import cn from '../../app/utils/cn';

const EMPTY_OPTION_VALUE = '__empty__';

interface ISelectProps {
  placeholder?: string;
  options: {
    value: string;
    text: string;
  }[];
  error?: string;
  emptyOptionLabel?: string;
  emptyOption?: boolean;
  value?: string;
  disabled?: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
  searchNoResultsText?: string;
  onChange?: (value: string) => void;
}

export default function Select({
  placeholder,
  options,
  error,
  emptyOption = false,
  emptyOptionLabel = '',
  value,
  disabled,
  searchable = false,
  searchPlaceholder,
  searchNoResultsText,
  onChange,
}: ISelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  const filteredOptions = useMemo(() => {
    if (!searchable) return options;
    const normalized = search.trim().toLowerCase();
    if (!normalized) return options;
    return options.filter((option) =>
      option.text.toLowerCase().includes(normalized),
    );
  }, [options, search, searchable]);

  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (!next) {
      setSearch('');
    } else if (searchable) {
      requestAnimationFrame(() => searchInputRef.current?.focus());
    }
  }

  function handleSelect(selected: string) {
    onChange?.(selected === EMPTY_OPTION_VALUE ? '' : selected);
  }

  const itemClass =
    'text-text-primary focus:bg-hover-subtle focus:data-[highlighted]:bg-hover-medium data-[state=checked]:bg-hover-subtle flex min-h-10 w-full cursor-pointer items-center justify-between gap-2 rounded p-2 text-sm transition-colors outline-none data-[state=checked]:font-semibold';

  return (
    <div className="relative w-full">
      <label
        className={cn(
          'text-text-disabled pointer-events-none absolute top-3 left-2.5 z-10 text-sm transition-all duration-300 ease-in-out',
          value && 'top-1 text-xs',
        )}
      >
        {placeholder}
      </label>
      <RdxSelect.Root
        value={value ?? ''}
        onValueChange={handleSelect}
        disabled={disabled}
        open={open}
        onOpenChange={handleOpenChange}
      >
        <RdxSelect.Trigger
          className={cn(
            'bg-surface-input border-border-input text-text-primary relative flex h-10 w-full items-center rounded border-2 border-solid pt-4 pr-8 pl-2 text-sm shadow-md outline-none focus:border-green-600 disabled:opacity-50 data-placeholder:pt-0 dark:focus:border-green-400',
            !!error && 'border-red-500',
            disabled && 'pointer-events-none',
          )}
        >
          <RdxSelect.Value />
          <RdxSelect.Icon className="text-text-tertiary absolute top-1/2 right-2 -translate-y-1/2">
            <ChevronDownIcon />
          </RdxSelect.Icon>
        </RdxSelect.Trigger>

        <RdxSelect.Portal>
          <RdxSelect.Content
            position="popper"
            sideOffset={4}
            className="bg-surface-card z-50 max-h-(--radix-select-content-available-height) w-(--radix-select-trigger-width) rounded p-2 shadow"
          >
            {searchable && (
              <div className="relative mb-1 px-1">
                <MagnifyingGlassIcon className="text-text-tertiary pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
                <input
                  ref={searchInputRef}
                  type="text"
                  autoFocus
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  onKeyDown={(event) => {
                    event.stopPropagation();
                    if (event.key === 'Enter') event.preventDefault();
                  }}
                  placeholder={searchPlaceholder ?? placeholder}
                  aria-label={searchPlaceholder ?? placeholder}
                  className="bg-surface-input border-border-input text-text-primary placeholder:text-text-tertiary w-full rounded border py-1.5 pr-2 pl-8 text-sm outline-none focus:border-green-600 dark:focus:border-green-400"
                />
              </div>
            )}

            <RdxSelect.ScrollUpButton className="text-text-tertiary flex h-6 cursor-default items-center justify-center">
              <ChevronUpIcon />
            </RdxSelect.ScrollUpButton>

            <RdxSelect.Viewport className="flex max-h-[min(var(--radix-select-content-available-height),15rem)] flex-col gap-2 overflow-y-auto p-2">
              {searchable && filteredOptions.length === 0 && (
                <p className="text-text-tertiary px-2 py-3 text-center text-sm">
                  {searchNoResultsText ?? ''}
                </p>
              )}
              {emptyOption && (
                <RdxSelect.Item
                  className={cn(itemClass, 'text-text-tertiary italic')}
                  value={EMPTY_OPTION_VALUE}
                >
                  <RdxSelect.ItemText>{emptyOptionLabel}</RdxSelect.ItemText>
                  <RdxSelect.ItemIndicator>
                    <CheckIcon />
                  </RdxSelect.ItemIndicator>
                </RdxSelect.Item>
              )}
              {filteredOptions.map((option) => (
                <RdxSelect.Item
                  className={itemClass}
                  value={option.value}
                  key={option.value}
                >
                  <RdxSelect.ItemText>{option.text}</RdxSelect.ItemText>
                  <RdxSelect.ItemIndicator>
                    <CheckIcon />
                  </RdxSelect.ItemIndicator>
                </RdxSelect.Item>
              ))}
            </RdxSelect.Viewport>

            <RdxSelect.ScrollDownButton className="text-text-tertiary flex h-6 cursor-default items-center justify-center">
              <ChevronDownIcon />
            </RdxSelect.ScrollDownButton>
          </RdxSelect.Content>
        </RdxSelect.Portal>
      </RdxSelect.Root>

      {error && (
        <div className="mt-1 flex items-center gap-1 text-xs text-red-500 dark:text-red-400">
          <CrossCircledIcon />
          <small>{error}</small>
        </div>
      )}
    </div>
  );
}
